export enum VisitaEstado {
    PENDIENTE = 1,
    REALIZADA = 2,
    FALLIDA = 3,
    CANCELADA = 4,
    RECHAZADO = 5,
    INFORMADO_SRT = 6, // Antes este valor era "BORRADOR".
    SIN_ASIGNAR = 7,
    PROGRAMADA = 8,
    EN_PROCESO = 9,
    PRESENTADO = 10,
    COMPLETADA = 11
}

// Estados en los que la visita ya fue ejecutada: el preventor no la puede editar y las
// descargas de la grilla se evalúan sobre estos estados. No define si la visita bloquea la
// creación de otra del mismo tipo: para eso está ESTADOS_VISITA_NO_BLOQUEANTES.
export const ESTADOS_VISITA_CONCLUIDOS: VisitaEstado[] = [
    VisitaEstado.REALIZADA,
    VisitaEstado.FALLIDA,
    VisitaEstado.CANCELADA,
    VisitaEstado.RECHAZADO,
    VisitaEstado.INFORMADO_SRT,
    VisitaEstado.PRESENTADO,
    VisitaEstado.COMPLETADA
];

// Estados en los que una visita ya no bloquea la creación de otra del mismo tipo. Es el mismo
// criterio que valida el back al crear (existeVisitaActivaEnEstablecimiento): una visita
// REALIZADA ("En Revisión") sigue abierta y bloquea.
export const ESTADOS_VISITA_NO_BLOQUEANTES: VisitaEstado[] = [
    VisitaEstado.FALLIDA,
    VisitaEstado.CANCELADA,
    VisitaEstado.RECHAZADO,
    VisitaEstado.PRESENTADO
];

export const esVisitaAbierta = (estado?: number): boolean =>
    !ESTADOS_VISITA_NO_BLOQUEANTES.includes(estado as VisitaEstado);

// Estados en los que se descargan el Acta de visita, la Verosimilitud y el Seguimiento de
// Verosimilitud. CANCELADA solo si el backend confirma que la visita tiene los datos de
// finalización completos (actaVerosimilitudDisponiblePorDatos).
export const esEstadoDescargaActaPermitido = (
    estado?: number,
    actaDisponiblePorDatos?: boolean
): boolean =>
    estado === VisitaEstado.PRESENTADO ||
    estado === VisitaEstado.REALIZADA ||
    (estado === VisitaEstado.CANCELADA && Boolean(actaDisponiblePorDatos));

export const VisitStatus: Record<VisitaEstado, string> = {
    [VisitaEstado.PENDIENTE]: 'PENDIENTE',
    [VisitaEstado.REALIZADA]: 'REALIZADA',
    [VisitaEstado.FALLIDA]: 'FALLIDA',
    [VisitaEstado.CANCELADA]: 'CANCELADA',
    [VisitaEstado.RECHAZADO]: 'RECHAZADO',
    [VisitaEstado.INFORMADO_SRT]: 'INFORMADO_SRT',
    [VisitaEstado.SIN_ASIGNAR]: 'SIN_ASIGNAR',
    [VisitaEstado.PROGRAMADA]: 'PROGRAMADA',
    [VisitaEstado.EN_PROCESO]: 'EN_PROCESO',
    [VisitaEstado.PRESENTADO]: 'PRESENTADO',
    [VisitaEstado.COMPLETADA]: 'COMPLETADA'
};

export enum VerosimilitudEstado {
    PENDIENTE = 1,
    REALIZADA = 2,
    BORRADOR = 3,
    PRESENTADO = 10
}

export const VerosimilitudStatus: Record<VerosimilitudEstado, string> = {
    [VerosimilitudEstado.PENDIENTE]: 'PENDIENTE',
    [VerosimilitudEstado.REALIZADA]: 'REALIZADA',
    [VerosimilitudEstado.BORRADOR]: 'BORRADOR',
    [VisitaEstado.PRESENTADO]: 'PRESENTADO'
};

export interface VisitaEstadoChipConfig {
    textColor: string;
    backgroundColor: string;
    text: string;
    borderColor?: string;
}

export const getVisitaEstadoChipSx = (config: VisitaEstadoChipConfig) => ({
    fontWeight: 700,
    color: config.textColor,
    backgroundColor: config.backgroundColor,
    borderRadius: '999px',
    border: `1px solid ${config.borderColor || config.textColor}`
});

export const visitaEstadoChipMap: Record<VisitaEstado, VisitaEstadoChipConfig> = {
    [VisitaEstado.PENDIENTE]: {
        textColor: '#384A52',
        backgroundColor: '#ECECEC',
        borderColor: '#A7B1B5',
        text: 'Pendiente'
    },
    [VisitaEstado.REALIZADA]: {
        textColor: '#175AB6',
        backgroundColor: '#EFF4FF',
        borderColor: '#8AB0E6',
        text: 'En Revisión'
    },
    [VisitaEstado.FALLIDA]: {
        textColor: '#F44336',
        backgroundColor: '#F9E7E7',
        borderColor: '#F1A7A1',
        text: 'Fallida'
    },
    [VisitaEstado.CANCELADA]: {
        textColor: '#D32F2F',
        backgroundColor: '#F9E7E7',
        borderColor: '#E8A0A0',
        text: 'Cancelada'
    },
    [VisitaEstado.RECHAZADO]: {
        textColor: '#D32F2F',
        backgroundColor: '#F9E7E7',
        borderColor: '#E8A0A0',
        text: 'Rechazado'
    },
    [VisitaEstado.INFORMADO_SRT]: {
        textColor: '#6B7280',
        backgroundColor: '#F3F4F6',
        borderColor: '#B8BDC6',
        text: 'Informado SRT'
    },
    [VisitaEstado.SIN_ASIGNAR]: {
        textColor: '#F97F29',
        backgroundColor: '#FFF1E8',
        borderColor: '#FBB785',
        text: 'Sin Asignar'
    },
    [VisitaEstado.PROGRAMADA]: {
        textColor: '#B35400',
        backgroundColor: '#FFEACC',
        borderColor: '#D49B66',
        text: 'Programada'
    },
    [VisitaEstado.EN_PROCESO]: {
        textColor: '#175AB6',
        backgroundColor: '#EFF4FF',
        borderColor: '#8AB0E6',
        text: 'En Proceso'
    },
    [VisitaEstado.PRESENTADO]: {
        textColor: '#126015',
        backgroundColor: '#D9EBC5',
        borderColor: '#8FBE92',
        text: 'Presentado'
    },
    [VisitaEstado.COMPLETADA]: {
        textColor: '#126015',
        backgroundColor: '#D9EBC5',
        borderColor: '#8FBE92',
        text: 'Completada'
    }
};
